import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHead } from "@/components/PageHead";
import { useQuery } from "@tanstack/react-query";

import { assetData, type AssetDataRecord, type AssetDataSignal, type AssetDataVal, type AssetDataLatest } from "@/api/client";
import { useCursorTail } from "@/lib/hooks/useCursorTail";
import { useSticky } from "@/lib/hooks/useSticky";
import { ago, POLL } from "@/lib/format";
import { ContextStrip } from "@/modules/asset_data/ContextStrip";
import { DevicePicker } from "@/modules/asset_data/picker";
import { RecordTable, fmt } from "@/modules/asset_data/RecordTable";

import { VehicleControlCard } from "@/modules/asset_data/VehicleControlCard";
import { Instrument } from "@/modules/asset_data/Instrument";
import { CellCharts } from "@/modules/asset_data/CellCharts";
import { PositionCard } from "@/modules/asset_data/PositionCard";
import { GeofenceCard } from "@/modules/asset_data/GeofenceCard";
import {
  FaultGrid, TripFoot, batteryGroups, vehicleGroups,
} from "@/modules/asset_data/FaultGrid";
// The tile rows went with the panels they belonged to: SoC, speed, pack
// voltage, current and the odometer are ON the instrument, the cell spread is
// in the charts, and the drive readouts are in the instrument's trios. What
// is left of `Tiers` here is the panel frame and the reference rows.
import { Panel } from "@/modules/asset_data/Tiers";

import { Icon } from "@/components/icons";
// Hard cap on rows held in the browser for the live tail. Bounds RAM on a tab
// left open for hours: at the 5–15 s snapshot cadence this is ~15–30 min of
// scrollback, and the buffer NEVER grows past it (oldest rows drop off).
const MAX_ROWS = 150;

/** One reference row: label left, value right, on ONE line.
 *
 *  THE MOCK USES `.kv` AND THIS PANEL DID NOT. It used `Reference`, which
 *  stacks the label above the value -- so "Asset & device" read as a column of
 *  twelve stacked pairs where the mock reads as three lists of labelled facts,
 *  and Dattatray spotted it immediately. `.kv` is Orbit's own atom
 *  (components.css U16) and `PositionCard` already uses it for exactly this.
 *
 *  "--" AND NEVER "0" OR BLANK where the value is absent (Chip.tsx's rule): a
 *  zero reads as a real reading and a blank reads as a rendering bug. The
 *  dash carries a hover saying WHY it is absent whenever the caller knows. */
function Kv({ k, v, mono = true, ok = false, nilHint }: {
  k: string;
  v: string | number | null | undefined;
  mono?: boolean;
  ok?: boolean;
  nilHint?: string;
}) {
  const absent = v == null || v === "";
  return (
    <div className="kv">
      <span>{k}</span>
      <b className={absent ? "lw-nil" : mono ? "mono" : undefined}
        style={!absent && ok ? { color: "var(--ok)" } : undefined}
        title={absent ? nilHint ?? "not reported" : undefined}>
        {absent ? "—" : String(v)}
      </b>
    </div>
  );
}
/** `batt_load_status`, straight from the proto's own description. Only the
 *  three values it names: an unlisted code renders as its number, because
 *  guessing which neighbour it meant is how a charging pack gets labelled
 *  idle. */
const LOAD: Record<number, string> = {
  0: "No load", 3: "Discharging", 4: "Charging",
};

// ---- tiny presentational helpers -------------------------------------------

// ---- value access + scaling -------------------------------------------------

type Vals = Record<string, AssetDataVal>;
function num(v: Vals, k: string): number | null {
  const x = v[k];
  return typeof x === "number" ? x : null;
}
function str(v: Vals, k: string): string | null {
  const x = v[k];
  return x == null || x === "" ? null : String(x);
}
const dash = (x: ReactNode) => (x == null || x === "" ? <span style={{ color: "var(--t-dim)" }}>—</span> : x);
// ============================================================================
//  DASHBOARD  — Device / Battery / Motor sections, then the record table
// ============================================================================

function Dashboard({ imei }: { imei: string }) {
  const q = useQuery({ queryKey: ["live", "latest", imei], queryFn: () => assetData.latest(imei), refetchInterval: POLL.STATE });
  // THE SAME QUERY KEY `ContextStrip` POLLS, so this adds no request:
  // react-query serves both from one cache entry. The registry facts the mock
  // shows -- plate, asset id, operator, driver, board serial, BLE MAC -- are
  // all on this endpoint and none of them is on `latest`.
  const cq = useQuery({
    queryKey: ["live", "context", imei],
    queryFn: () => assetData.context([imei]),
    refetchInterval: POLL.LIST,
    enabled: !!imei,
  });
  const ctx = cq.data?.context?.[imei];
  const l = q.data?.latest as AssetDataLatest | undefined;
  if (!l) return <div className="panel panel-pad cap">No snapshot for this device yet.</div>;
  const v = l.values;

  // LOCATION, FROM THE SERVER'S `position` BLOCK — not from `values`.
  //
  // `values.lat/lng` is the PACKET as decoded, and it is correctly NULL when
  // the device reported no fix (D-07). Reading it here is what made the card
  // say "No GPS fix yet, location unavailable" for a bike that had been
  // parked in the same spot for an hour: true about the packet, useless about
  // the asset. `position` answers the other question — where it was last, and
  // how long ago — and says which of the two it is handing back.
  const pos = l.position;
  // `hasGps`, the map URL and the fix label moved into `PositionCard` with
  // the card they belonged to; `charging` moved into `Instrument`, which draws
  // the current with its direction. What is left here is what the page itself
  // still renders: the two clocks, and the dial inputs.
  // A RESPONSE WITHOUT THE NEW BLOCKS MUST NOT BLANK THE PAGE. During a
  // rolling deploy the bundle is newer than the server for a few seconds, and
  // an older server sends no `bitfields` and no `cells_mv`; reading through
  // `undefined` threw and took the whole dashboard with it, which is a far
  // worse failure than a panel saying "not reported".
  const bits = l.bitfields ?? {};
  const cells = l.cells_mv ?? null;
  // The pack's lifetime 0xF7 counters. Each wraps 255 -> 0, so a FALL is not a
  // repair; nothing here does arithmetic on them for that reason.
  const counters = {
    batt_ov_count: num(v, "batt_ov_count"),
    batt_uv_count: num(v, "batt_uv_count"),
    batt_cuv_count: num(v, "batt_cuv_count"),
    batt_occ_count: num(v, "batt_occ_count"),
    batt_ocd_count: num(v, "batt_ocd_count"),
    batt_sc_count: num(v, "batt_sc_count"),
    batt_reset_count: num(v, "batt_reset_count"),
  };
  const soc = num(v, "soc");
  const speed = num(v, "speed_kmph");

  // SoC IS A MAGNITUDE THAT IS ALSO A STATUS, which is the one case a value
  // earns a status colour. That rule did not change; the threshold moved INTO
  // `Instrument`, which colours the charge RING from the same bands (15 bad,
  // 35 warn) now that the ring is where state of charge is read.
  // Everything else on this page stays in text ink: a dashboard where every
  // number is coloured has not told you which one to look at.

  return (
    <>
      {/* ═══════════ IDENTITY BAR ═══════════
          Who this is, and the two clocks. Deliberately quiet: it is the
          page's address label, not its content. */}
      <div className="panel panel-pad" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {/* ONLINE IS STATUS, so it keeps a status colour: with a glyph and a
            word, because `--ok` and `--warn` are ΔE 4.1 apart under
            protanopia and the colour alone is not legible to every reader. */}
        <span className="chip" style={{
          color: l.online ? "var(--ok)" : "var(--warn)",
          background: l.online ? "var(--ok-soft)" : "var(--warn-soft)",
        }}>{l.online ? "● ONLINE" : "○ STALE"}</span>
        {/* app version is NOT status. It was wearing `--info`, the info
            colour, for no reason other than that blue looked fine. */}
        {l.fw_ver && <span className="chip" style={{ color: "var(--t-mute)", background: "var(--well)" }}>app {l.fw_ver}</span>}
        <ContextStrip imei={l.imei} />
        {/* THE IMEI, AND NOTHING ELSE FROM THE OLD IDENTITY STRIP. That strip
            printed Asset ID, Number plate and IMEI directly beneath this bar,
            and `ContextStrip` two elements to the left already prints the
            asset id and the plate -- so the page stated both twice, from two
            components, neither aware of the other. That is the bug
            `ContextStrip` already carries a comment about for `company`; this
            is the same shape one level up, and the fix is the same: keep the
            fact once, in the component that owns it.
            The IMEI was the only value the strip added, so it moves here and
            the strip goes. It is `.mono` because it gets read down a phone or
            pasted into a ticket, which is what the strip's tabular figures
            were for. */}
        <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
          imei <span className="mono" style={{ color: "var(--t-body)", fontWeight: 600 }}>{l.imei}</span>
        </span>
        {l.company_id && <span className="muted" style={{ fontSize: 11.5 }}>· {l.company_id === "__inventory__" ? "inventory" : l.company_id}</span>}
        <span className="muted" style={{ marginLeft: "auto", fontSize: 11.5 }}>
          {/* TWO CLOCKS, never collapsed into one: the DEVICE spoke `seen` ago,
              the POSITION was true `GPS` ago. A bike parked indoors is online
              with an hour-old fix, and one number cannot say which it is. */}
          seq {l.seq ?? "—"} · seen {ago(l.last_seen)} ago
          {pos?.at ? <> · GPS {ago(pos.at)} ago{!pos.fix ? " (last known)" : ""}</> : null}
        </span>
      </div>

      {/* ═══════════ THE DASHBOARD ═══════════
          TWO PACKING COLUMNS, not panels dropped onto a 12-column grid.
          Panels placed straight onto the grid SHARE its rows, so a short one
          leaves a hole the height of whatever sits beside it: Vehicle Control
          is far shorter than the instrument, and Location was being pushed
          down to clear it. Each side is its own column, so the boxes stack
          against each other and the two sides are independent.

          The order is what an operator asks, not what the record contains:
          can it move, where is it (left); what is it doing, how is the pack
          (right). `Panel` wrappers stay -- they carry the fault counts and
          the collapse behaviour the rest of the console shares.

          "Right now" IS GONE as a separate tier: SoC, speed, pack voltage,
          current and the odometer are all ON the instrument, and keeping the
          tile row as well would state each of them twice. */}
      <div className="lw-dash">
      <div className="lw-col lw-col-l">
      <VehicleControlCard vc={l.vehicle_control} fresh={l.online} />

      {/* ═══════════ WHERE IT IS ═══════════
          A REAL basemap, the derived HDOP confidence, and the fix rows. The
          three sentences `Position.test.tsx` asserts live in the card. */}
      <PositionCard v={v} pos={l.position} />

      {/* ═══════════ GEOFENCING ═══════════
          READ ONLY. Setting one lives in Fleet, behind GATE_FLEET +
          IMMOBILIZE; GATE_ASSET_DATA is in every plan including basic, so a
          create control here would put "stop a vehicle, later, unattended"
          behind the weaker gate (OFL-07a).

          IT IS HERE BECAUSE THIS PAGE ALREADY SAYS "held by Fleet manager
          (2), reason 1" -- and if a fence placed that hold, the fence is the
          explanation. Without it an operator reads the hold on this page and
          has to leave the module to find out why. */}
      <GeofenceCard assetId={ctx?.asset?.asset_id ?? null}
        position={l.position?.lat != null && l.position?.lng != null
          ? { lat: l.position.lat, lng: l.position.lng } : null} />
      </div>

      <div className="lw-col lw-col-r">
      {/* THE INSTRUMENT. Ported from the cluster meter so a rider and a fleet
          operator looking at the same vehicle see the same shapes. */}
      <section className="panel" style={{ overflow: "visible", background: "none", border: "none" }}>
        <Instrument v={v} soc={soc} speed={speed} bits={bits}>
          {/* flush inside the frame: the housing continuing downward */}
          <FaultGrid title="Faults" groups={vehicleGroups(bits)} />
        </Instrument>
      </section>

      {/* ═══════════ HOW THE PACK IS ═══════════
          No charge ring here: the instrument's left dial already carries state
          of charge. These are the facts the dial cannot show. */}
      <Panel title="Battery pack">
        <div className="livegrid">
          <div><div className="k">Health</div>
            <div className="v ok">{fmt(num(v, "batt_soh_pct") ?? "\u2014")}<span className="u">%</span></div>
            <div className="s">SoH</div></div>
          <div><div className="k">Cycles</div>
            <div className="v">{fmt(num(v, "batt_cycle_count") ?? "\u2014")}</div></div>
          <div><div className="k">Capacity</div>
            <div className="v">{num(v, "batt_capacity_mah") != null
              ? (num(v, "batt_capacity_mah")! / 1000).toFixed(1) : "\u2014"}<span className="u"> Ah</span></div></div>
          <div><div className="k">Cells</div>
            <div className="v">{fmt(num(v, "cells") ?? "\u2014")}</div></div>
          {/* IT WAS NEVER UNDECODED -- I claimed that and was wrong. The
              proto documents this field on its own line: "BMS load state:
              0=NoLoad 3=Discharging 4=Charging". It is also what detects a
              charger being unplugged (4 -> 0), one of the two station events
              Dattatray wants the cell extremes captured at. 1 and 2 are not in
              the proto's list, so they render as themselves rather than being
              folded into a neighbour they might not mean. */}
          <div><div className="k">Load status</div>
            <div className="v sm">{LOAD[num(v, "batt_load_status") ?? -1]
              ?? dash(num(v, "batt_load_status"))}</div>
            <div className="s">BMS load state</div></div>
          <div><div className="k">Vendor</div>
            <div className="v sm">{dash(str(v, "batt_company_id"))}</div>
            <div className="s">1 Battery Smart, 0 Aphelion</div></div>
        </div>

        <div className="section-label">Cells</div>
        <CellCharts v={v} cells={cells} />

        {/* ONE CELL PER CONDITION, escalating: amber when the pack reports the
            alarm that PRECEDES a protection, red once the protection fires. */}
        {/* THE COUNTERS GO ON THE CONDITIONS, not in a panel of their own.
            Read once, handed to the grid -- which puts four of the seven in
            front of their own condition -- and to the footer, which holds the
            three that name no single condition. */}
        <FaultGrid title="Battery errors" groups={batteryGroups(bits, counters)}
          foot={<TripFoot c={counters} />} />
      </Panel>
      </div>
      </div>

      {/* ═══════════ ASSET & DEVICE ═══════════
          FULL WIDTH, and last. These are true for months: an IMEI is the same
          tomorrow and a board ID was decided at manufacture. They were nine
          rows at the TOP of the page once, which made the most static block
          on it the loudest.

          OPEN, not folded. In the dashboard it is one row at the foot rather
          than a tier competing with live readings, so hiding it buys nothing
          and costs a click. Three columns, because the width is there: what
          the thing IS, what software it runs, and what identifies it. */}
      <section className="panel lw-wide">
        <div className="lw-vc-top"><span className="lw-vc-lbl">Asset &amp; device</span></div>
        <div className="lw-vc-sec" style={{ borderTop: "none", paddingTop: 0 }}>
          <div className="lw-trio">
            <div>
              <div className="section-label flush">Asset</div>
              {/* THE TAGS the mock carries: what kind of thing this is, and
                  the life stage of the board inside it. */}
              <div className="lw-tags">
                {ctx?.asset && (
                  <span className="chip" style={{ color: "var(--t-mute)", background: "var(--well)" }}>
                    {ctx.asset.asset_type === "vehicle" ? "Vehicle" : "Battery"}
                  </span>
                )}
                {l.device_state && (
                  <span className="chip" style={{ color: "var(--info)", background: "var(--info-soft)" }}>
                    {l.device_state}
                  </span>
                )}
              </div>
              {/* ORDER IS THE HIERARCHY the mock states: plate, asset id,
                  operator, driver -- what you identify the thing by first,
                  then who runs it, then who is on it. The plate leads only
                  BECAUSE the type is vehicle; a battery has none, so Asset ID
                  leads there instead. */}
              <div>
                {ctx?.asset?.asset_type !== "battery" && (
                  <Kv k="Number plate" v={ctx?.asset?.label ?? l.vehicle_reg} />
                )}
                <Kv k="Asset ID" v={ctx?.asset?.asset_id} />
                <Kv k="Operator" v={ctx?.company ?? l.company_id} mono={false} />
                <Kv k="Allocated driver" mono={false}
                  v={ctx?.driver ? `${ctx.driver.name} (${ctx.driver.code})` : null}
                  nilHint="no driver is allocated to this asset" />
              </div>
            </div>
            <div>
              <div className="section-label flush">Software</div>
              <div>
                <Kv k="Host app" v={num(v, "app_sw_version") ?? l.fw_ver} ok />
                <Kv k="Host bootloader" v={num(v, "boot_sw_version") ?? l.bl_ver} />
                {/* THE ONLY SOURCE for the module's version is the health
                    row's per-node map; the telemetry record has no BLE
                    version field at all. */}
                <Kv k="BLE module" v={l.plane_vers?.ble ?? null}
                  nilHint="no BLE node version in this device's last heartbeat" />
                <Kv k="HW revision" v={l.hw_rev ?? num(v, "hw_version")} />
                <Kv k="Running mode" mono={false}
                  v={l.run_mode === "app" ? "Application"
                    : l.run_mode === "boot" ? "Bootloader" : l.run_mode}
                  nilHint="Orbit has not had a FOTA heartbeat from this device" />
                {/* NULL IS UNKNOWN, NOT REFUSED. A device in boot mode omits
                    the field because it has no veto to give, and telling an
                    operator "not allowed" when the answer is "we do not know"
                    is the wrong way round. */}
                <Kv k="FOTA" mono={false}
                  v={l.fota_allowed == null ? null : l.fota_allowed ? "Allowed" : "Held by device"}
                  ok={l.fota_allowed === true}
                  nilHint="unknown: the device has not reported its FOTA veto" />
                {/* The BLE MODULE's own firmware, which is a different thing
                    from the application build above it and has its own FOTA
                    path. Absent on builds before app 57. */}
                <Kv k="BLE firmware" v={l.ble_module?.sw_version}
                  nilHint="the device has not reported its BLE module version (app 57+)" />
              </div>
            </div>
            <div>
              <div className="section-label flush">Identity</div>
              <div>
                <Kv k="Board serial" v={ctx?.part?.serial_no} />
                <Kv k="IMEI" v={l.imei} />
                <Kv k="BLE MAC" v={ctx?.part?.ble_mac}
                  nilHint="no BLE MAC recorded against this part" />
                {/* WHAT THE DEVICE ITSELF REPORTS, shown only when it
                    DISAGREES with the registry. Two sources for one fact, and
                    the whole value is the mismatch: agreement is silent, a
                    difference means the registry is describing a module that
                    is not in this unit -- a swapped board, or a mis-entered
                    MAC at enrolment.

                    The wire carries it LSB first and the registry stores the
                    opposite order; the server reverses before comparing, so a
                    difference here is a real difference and not a byte-order
                    artefact. */}
                {l.ble_module?.mac && ctx?.part?.ble_mac
                  && l.ble_module.mac.toUpperCase() !== ctx.part.ble_mac.toUpperCase() && (
                  <Kv k="BLE MAC (reported)" v={`${l.ble_module.mac}  ⚠ differs from the part record`} />
                )}
                <Kv k="ICCID" v={l.iccid} />
              </div>
            </div>
          </div>
          {/* ═══ POWER ═══ a second row rather than a fourth column: these
              are not identity, they change, and at three columns the trio is
              already at the width where a long reset-cause name wraps.

              DECODED FROM FIELD 69, app 57+. Absent entirely on older builds,
              and absent reads as absent rather than as "no resets". */}
          {l.power && (
            <div className="lw-trio" style={{ marginTop: 14 }}>
              <div>
                <div className="section-label flush">Power</div>
                <div>
                  {/* "unverified" when the build is not known to hand the
                      cause over: below app 58 the app's own read is always
                      0x1D, so naming it would state a fact the record cannot
                      support. The hover says why rather than leaving a word
                      nobody can act on. */}
                  {/* "unverified" when the build does not hand the cause
                      over (app 58+ does): below that the app's own read is
                      always 0x1D, so naming it would state a fact the record
                      cannot support. The word carries that itself rather
                      than needing a prop the shared Kv does not have. */}
                  <Kv k="Last reset" mono={false}
                    v={l.power.reset_cause_trusted
                      ? l.power.reset_cause
                      : "unverified (build below app 58)"}
                    nilHint="the device has not reported a reset cause" />
                  <Kv k="Power state" mono={false} v={l.power.power_state} />
                  {/* AN EMPTY LIST IS A FACT: no sleep since the last reset,
                      which is exactly what a bench board or a vehicle in
                      continuous use reports. Said in words rather than left
                      blank. */}
                  <Kv k="Last wake" mono={false}
                    v={l.power.wake_flags.length
                      ? l.power.wake_flags.join(", ")
                      : "no sleep since reset"} />
                </div>
              </div>
              <div>
                <div className="section-label flush">BLE module</div>
                <div>
                  <Kv k="Reset cause" mono={false} v={l.power.ble_reset_cause}
                    nilHint="the module has not completed a HELLO_BLE since the MCU reset, so its cause is not yet valid" />
                  {/* THE GENERATION THE VEHICLE ACCEPTED. Orbit issues
                      grants and the device reports which it took; a device
                      ahead of Orbit's record is the D-75 signal. Detection
                      is not wired yet, so this is the number, not a verdict. */}
                  <Kv k="Grant generation" v={l.ble_module?.grant_gen ?? null}
                    nilHint="the device has not reported a grant generation (app 57+)" />
                </div>
              </div>
              <div />
            </div>
          )}
        </div>
      </section>
    </>
  );
}


/** Live-tailing decoded record table (newest on top) — the tail itself is the
 *  shared useCursorTail hook (single-flight, hidden-guard, dedupe, bounded). */
function LiveTail({ imei }: { imei: string }) {
  const [signals, setSignals] = useState<AssetDataSignal[]>([]);
  const [paused, setPaused] = useState(false);

  const { items: records, clear } = useCursorTail<AssetDataRecord>({
    fetch: async (cursor) => {
      const { records: batch, signals: sig } = await assetData.records(imei, cursor as string | undefined);
      // signals = the static catalog, sent with every response — adopt it ONCE
      // (fresh identity every tick re-rendered the whole table; the render-storm bug)
      if (sig.length) setSignals((prev) => (prev.length ? prev : sig));
      return batch;
    },
    cursorOf: (r) => r.cursor,
    max: MAX_ROWS,
    paused,
    resetKey: imei,
  });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 0 8px" }}>
        <div className="section-label flush">Decoded records (live tail)</div>
        {paused && <span style={{ color: "var(--warn)", fontWeight: 700, fontSize: 11 }}>❚❚ PAUSED</span>}
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--t-mute)" }}>{records.length} rows</span>
        <button className={`btn ${paused ? "teal" : "ghost"}`} onClick={() => setPaused((p) => !p)}>
          <Icon name={paused ? "play" : "pause"} /> {paused ? "Resume" : "Pause"}
        </button>
        <button className="btn ghost" onClick={clear}>Clear</button>
      </div>
      <RecordTable records={records} signals={signals} newestFirst />
    </div>
  );
}

export function AssetDataWindowPage() {
  // Sticky across navigation: leave Live Data and come back → same device.
  const [imei, setImei] = useSticky("live", "imei", "");
  // ...but `?imei=` WINS, once. That is how the fleet map hands a device over:
  // click a dot, land here already watching it. The param is then cleared, so
  // a later pick from the picker is not fought by a stale URL on re-render.
  const [params, setParams] = useSearchParams();
  const deep = params.get("imei");
  useEffect(() => {
    if (!deep) return;
    setImei(deep);
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete("imei"); return p; },
              { replace: true });
  }, [deep]);
  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Data · Live Window" title="Watch a device"
        desc={<>Pick a company and IMEI to watch its decoded telemetry - device identity, battery and motor dials up top, then a live-tailing record table.</>} />

      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <DevicePicker imei={imei} onPick={setImei} />
      </div>

      {imei ? (
        <>
          <Dashboard imei={imei} />
          <LiveTail imei={imei} />
        </>
      ) : (
        <div className="panel panel-pad cap">
          Select a device above to start watching.
        </div>
      )}
    </div>
  );
}
